<script lang="ts">
    import { get } from 'svelte/store';
    import BetCard from './bet-card.svelte';
    import CentralSector from './central-sector.svelte';
    import sectorOneImg from '@src/assets/img/sector_1.webp';
    import sectorTwoImg from '@src/assets/img/sector_2.webp';
    import sectorFiveImg from '@src/assets/img/sector_5.webp';
    import sectorTenImg from '@src/assets/img/sector_10.webp';
    import sectorBallSpinImg from '@src/assets/img/sector_ball_spin.webp';
    import sectorPlinkoImg from '@src/assets/img/sector_plinko.webp';
    import sectorCashReelsImg from '@src/assets/img/sector_cash_reels.webp';
    import sectorFortuneWheelImg from '@src/assets/img/sector_fortune_wheel.webp';
    import PatternLine from '../svg-patterns/pattern-line.svelte';
    import { betLeft, betRight, getBetPerColor, placeBet } from '@root/src/betting/betting';
    import { selectedChipIndex, availableChips, chipBetPerColor, isMaxedOut } from '@root/src/store/chips';
    import { EventEmitterInstance } from '@root/src/utils/event-emitter';
    import { gameState, gameStatus, isUndoResetHistory } from '@root/src/store';
    import { PositionType } from '@root/src/types';
    import BetHint from '../hint/bet-hint.svelte';
    import { gameStates } from '@root/src/states/machine/state-types';
    import MaxBet from '../badges/max-bet.svelte';

    let classImgTop = '-translate-y-3 desktop:-translate-y-4';
    let classImgBottom = 'translate-y-3 desktop:translate-y-4';

    let classImgValues = 'scale-90';

    type BetCards = {
        classesCard: string;
        chipPosition: PositionType;
        isMaxBetPositionBottom: boolean;
        imgSrc: string;
        classesImg: string;
        patternLineClasses?: string;
    }[];
    const leftBetCards: BetCards = [
        {
            classesCard: 'bg-sector-black',
            chipPosition: 'left-top',
            isMaxBetPositionBottom: true,
            imgSrc: sectorOneImg,
            classesImg: classImgValues
        },
        {
            classesCard: 'bg-sector-light-blue',
            chipPosition: 'right-top',
            isMaxBetPositionBottom: true,
            imgSrc: sectorTwoImg,
            classesImg: classImgValues
        },
        {
            classesCard: 'bg-sector-purple',
            chipPosition: 'left-bottom',
            isMaxBetPositionBottom: false,
            imgSrc: sectorFiveImg,
            classesImg: classImgValues
        },
        {
            classesCard: 'bg-sector-orange',
            chipPosition: 'right-bottom',
            isMaxBetPositionBottom: false,
            imgSrc: sectorTenImg,
            classesImg: classImgValues
        }
    ];

    const RightBetCards: BetCards = [
        {
            classesCard: 'bg-sector-pink',
            chipPosition: 'left-top',
            isMaxBetPositionBottom: true,
            imgSrc: sectorBallSpinImg,
            classesImg: classImgBottom,
            patternLineClasses: 'absolute inset-0'
        },
        {
            classesCard: 'bg-sector-blue',
            chipPosition: 'right-top',
            isMaxBetPositionBottom: true,
            imgSrc: sectorPlinkoImg,
            classesImg: classImgBottom,
            patternLineClasses: 'absolute inset-0 -scale-x-100'
        },
        {
            classesCard: 'bg-sector-yellow',
            chipPosition: 'left-bottom',
            isMaxBetPositionBottom: false,
            imgSrc: sectorCashReelsImg,
            classesImg: classImgTop,
            patternLineClasses: 'absolute inset-0 -scale-x-100'
        },
        {
            classesCard: 'bg-sector-green',
            chipPosition: 'right-bottom',
            isMaxBetPositionBottom: false,
            imgSrc: sectorFortuneWheelImg,
            classesImg: classImgTop,
            patternLineClasses: 'absolute inset-0'
        }
    ];

    const getBet = () => {
        const $availableChips = get(availableChips);
        const $selectedChip = get(selectedChipIndex);

        return Number($availableChips[$selectedChip]);
    };

    const sectorBetHandler = (index = 0) => {
        isUndoResetHistory.set(false);
        placeBet(index, getBet());
        chipBetPerColor.set(getBetPerColor());
        EventEmitterInstance.emit('analytics-bet-ui-interaction', index);
    };

    const sectorLeftBetAll = () => {
        betLeft(getBet());
        chipBetPerColor.set(getBetPerColor());
        EventEmitterInstance.emit('analytics-bet-ui-interaction-center-left');
    };

    const sectorRightBetAll = () => {
        betRight(getBet());
        chipBetPerColor.set(getBetPerColor());
        EventEmitterInstance.emit('analytics-bet-ui-interaction-center-right');
    };

    $: isAllMaxLeft = $isMaxedOut.slice(0, 4).every(isMax => isMax);
    $: isAllMaxRight = $isMaxedOut.slice(4).every(isMax => isMax);
</script>

<div class="position-container adaptive-px bets-container absolute z-1000 w-full max-w-xl">
    {#if $gameState === gameStates.game && $gameStatus === 'PLACE_BET'}
        <BetHint />
    {/if}

    <div class="grid grid-cols-2 gap-1 desktop:gap-1.5">
        <div class="relative grid grid-cols-2 grid-rows-2 gap-1 desktop:gap-1.5">
            <div class=" absolute left-1/3 top-1/3 z-1000 h-2/6 w-2/6 bg-cover bg-no-repeat">
                <button class="btn-base hover:brightness-125" on:click={sectorLeftBetAll} disabled={isAllMaxLeft}>
                    <CentralSector variant="left" />
                </button>
            </div>

            {#each leftBetCards as card, index}
                <BetCard
                    class={card.classesCard}
                    chipPosition={card.chipPosition}
                    chipValue={$chipBetPerColor[index] > 0 ? $chipBetPerColor[index].toString() : ''}
                    chipIndex={$selectedChipIndex}
                    disabled={$isMaxedOut[index]}
                    on:click={() => {
                        sectorBetHandler(index);
                    }}
                >
                    <img src={card.imgSrc} alt="sector bet" class={card.classesImg} />

                    {#if $isMaxedOut[index]}
                        <MaxBet isPositionBottom={card.isMaxBetPositionBottom} />
                    {/if}
                </BetCard>
            {/each}
        </div>

        <div class="relative grid grid-cols-2 grid-rows-2 gap-1 desktop:gap-1.5">
            <div class=" absolute left-1/3 top-1/3 z-1000 h-2/6 w-2/6 bg-cover bg-no-repeat">
                <button class="btn-base hover:brightness-125" on:click={sectorRightBetAll} disabled={isAllMaxRight}>
                    <CentralSector variant="right" />
                </button>
            </div>

            {#each RightBetCards as card, index}
                <BetCard
                    class={card.classesCard}
                    chipPosition={card.chipPosition}
                    chipValue={$chipBetPerColor[index + 4] > 0 ? $chipBetPerColor[index + 4].toString() : ''}
                    chipIndex={$selectedChipIndex}
                    disabled={$isMaxedOut[index + 4]}
                    on:click={() => {
                        sectorBetHandler(index + 4);
                    }}
                >
                    <PatternLine class={card.patternLineClasses} />
                    <img src={card.imgSrc} alt="sector bet" class={card.classesImg} />

                    {#if $isMaxedOut[index + 4]}
                        <MaxBet isPositionBottom={card.isMaxBetPositionBottom} />
                    {/if}
                </BetCard>
            {/each}
        </div>
    </div>
</div>

<style>
    .position-container {
        bottom: calc(var(--controls-container-height) + var(--footer-height) + 24px);
    }
</style>
